import { createClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

// Shared studio types — used in client + server code.


export type SurfaceKind =
  | "walls"
  | "ceiling"
  | "trim"
  | "doors"
  | "cabinets"
  | "windows"
  | "brick"
  | "siding"
  | "garage-doors"
  | "shutters"
  | "fence"
  | "deck";

export const SURFACE_LABELS: Record<SurfaceKind, string> = {
  walls: "Walls",
  ceiling: "Ceiling",
  trim: "Trim",
  doors: "Doors",
  cabinets: "Cabinets",
  windows: "Window frames",
  brick: "Brick",
  siding: "Siding",
  "garage-doors": "Garage doors",
  shutters: "Shutters",
  fence: "Fence",
  deck: "Deck",
};

export const ALL_SURFACES: SurfaceKind[] = [
  "walls", "ceiling", "trim", "doors", "cabinets", "windows",
  "brick", "siding", "garage-doors", "shutters", "fence", "deck",
];

export type DetectedSurface = {
  kind: SurfaceKind;
  // Brief location/description from vision model, e.g. "front-facing large wall left of fireplace".
  description: string;
  // 0-1 confidence.
  confidence: number;
};

export type SurfaceAssignment = {
  kind: SurfaceKind;
  colorId: string;
  colorName: string;
  colorNumber: string;
  brand: string;
  hex: string;
};

export type StudioProject = {
  id: string;
  name: string;
  source_image_url: string;
  source_image_width: number | null;
  source_image_height: number | null;
  detected_surfaces: DetectedSurface[];
  created_at: string;
  updated_at: string;
};

export type StudioVersion = {
  id: string;
  project_id: string;
  label: string;
  rendered_image_url: string;
  surface_assignments: SurfaceAssignment[];
  created_at: string;
};

// Anonymous browser token — keeps guest projects scoped to this browser.
export function getAnonToken(): string {
  if (typeof window === "undefined") return "";
  const k = "mp_studio_anon";
  let v = window.localStorage.getItem(k);
  if (!v) {
    v = crypto.randomUUID();
    window.localStorage.setItem(k, v);
  }
  return v;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/**
 * Returns the shared Supabase client for signed-in users, or a dedicated
 * anonymous client that sends the browser's anon token as a request header.
 * Guest RLS policies on studio_projects require this header so a guest can
 * only touch projects created with the same browser token.
 */
export async function getStudioClient() {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user) return supabase;
  return createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        "x-anon-token": getAnonToken(),
      },
    },
  });
}
